import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';
import 'react-datepicker/dist/react-datepicker.css';

import ActualCostsAllocationRow from './ActualCostsAllocationRow';
import BulkApplyHint from './BulkApplyHint';
import BulkApplyResultBar from './BulkApplyResultBar';

import Button from 'CommonComponents/Button';
import CheckboxInput from 'CommonComponents/CheckboxInput';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import TableFilterModal from 'CommonComponents/TableFilterModal';
import TableFilterModalThreeHandlers from 'CommonComponents/TableFilterModalThreeHandlers';
import TablePagination from 'CommonComponents/TablePagination';
import TableSearchModal from 'CommonComponents/TableSearchModal';

import useApiRequest from 'Hooks/useApiRequest';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';
import MinusIcon from 'Assets/icons/minus.svg';
import PlusIcon from 'Assets/icons/plus.svg';
import RefreshSync from 'Assets/icons/refresh-sync.svg';

const defaultVals = {
    documentType: true,
    documentNumber: false,
    transactionDate: true,
    glCode: true,
    rawAmount: true,
    totalExcludeTax: false,
    tax: false,
    total: false,
    taxExcluded: false,
    taxRate: false,
    type: true,
    transactionType: true,
    department: true,
    budgetLine: true,
    contractReference: true,
    amount: true,
    splitAllocation: true,
};

const EMPTY_KEY = 'Empty';

/**
 * Bulk-apply column → the allocation-input field it writes. Mirrors
 * App\Enums\BulkApplyColumn::allocationField() so an applied value can be patched
 * into a row's unsaved in-memory allocation.
 */
const BULK_APPLY_FIELDS = {
    department: 'typed_allocatable_id',
    budget_line: 'typed_valuable_id',
    contract: 'typed_contractable_id',
    type: 'type',
};

/**
 * The columns bulk apply offers, per table.
 *
 * Contract reference alone on the reconciled table: `reconciled` is recomputed from
 * a row's allocations on every write, so a department or budget line applied there
 * can leave a row no longer fully allocated and take it off the very table it was
 * applied from. Contract reference is not part of that computation, so it cannot.
 * Enforced server-side too — BulkApplyCriteriaRequest rejects any other column with
 * `reconciled` true, so this list can only ever hide a trigger, never authorise one.
 */
const BULK_APPLY_COLUMNS = Object.keys(BULK_APPLY_FIELDS);

const BULK_APPLY_COLUMNS_RECONCILED = ['contract'];

const COLUMN_FILTER_TYPES = ['suppliers', 'glCodes', 'documentTypes', 'departments', 'budgetLines', 'contractReferences'];

/**
 * Keys of a department / budget-line / contract-reference filter dict, minus
 * `Empty`.
 *
 * `combineTableFilters` synthesises an `Empty` entry into these dicts for any row
 * that is genuinely unallocated OR carries an id the frontend cannot resolve — the
 * ids are indistinguishable once a row has no allocation. IB-835 removed
 * "Unallocated" as an option from these header filters entirely (Verified
 * Requirement #1), so the checkbox list must never render it: doing so would let a
 * reconciler untick it and, since the backend's Empty payload key was made to
 * default `show: true` specifically to cover the "no way to hide these" case
 * (ActualCostsService::getActualCostsQuery), that untick would inconsistently hide
 * unallocated rows depending on whether some UNRELATED row on the page happened to
 * be unresolvable. Excluding it here — rather than deleting the entry outright —
 * keeps its `show` value pinned to the default the checkbox can never touch.
 */
const filterableKeys = (dict) => Object.keys(dict).filter((key) => key !== EMPTY_KEY);

/**
 * Counts alongside a month group heading, e.g. "— 14 items, 3 reconciled".
 *
 * The aggregate is month-wide and event-wide and spans BOTH reconciliation
 * states, so "3 reconciled" stays meaningful even though this table only renders
 * one of the two. Header filters and searches narrow the page but not the
 * aggregate, so while one is active the count of rows actually on screen is shown
 * too — the numbers differ legitimately, and saying so beats looking wrong.
 */
function MonthCountsBadge({ counts, shown, showShown }) {
    if (!counts) {
        return null;
    }

    const text = showShown
        ? $t('{{items}} items ({{shown}} shown), {{reconciled}} reconciled', { items: counts.items, shown, reconciled: counts.reconciled })
        : $t('{{items}} items, {{reconciled}} reconciled', { items: counts.items, reconciled: counts.reconciled });

    return <span className="tw-pe-3 tw-text-xs tw-font-normal tw-text-charcoal-75">— {text}</span>;
}

function ActionsColumnHeader({
    isSelectionMode,
    selectedItems,
    totalEligibleCount,
    buildConfirmPayload,
    confirmActualCosts,
    actualCostType,
    exitSelectionMode,
    setRowAllocations,
    setEditedRows,
    confirmAllLoading,
    confirmAllDisabled,
    enableSelectionMode,
    isReconciled,
}) {
    return (
        <div className="tw-flex tw-items-center tw-justify-center tw-gap-x-1.5">
            {isSelectionMode ? (
                <Fragment>
                    <span className="tw-text-xs">{$t('{{selected}} of {{total}} selected', { selected: selectedItems.size, total: totalEligibleCount })}</span>
                    <Button
                        variant="primary"
                        small
                        onClick={() => {
                            const { pending, ids } = buildConfirmPayload();
                            const skippedCount = Math.max(0, totalEligibleCount - (pending.length + ids.length));
                            confirmActualCosts({ pending, ids }, actualCostType, skippedCount, isReconciled).then((result) => {
                                if (result !== null) {
                                    setRowAllocations((prev) => {
                                        const next = { ...prev };
                                        // Only remove allocations for IDs the server actually confirmed.
                                        // Skipped rows keep their in-memory edits so the user can fix
                                        // and retry without losing their unsaved allocation changes.
                                        result.forEach((id) => delete next[id]);
                                        return next;
                                    });
                                    // Return confirmed rows to read state, mirroring what the
                                    // per-row tick does via setEditedRow(id, false). Skipped rows
                                    // stay in edit mode so the user can fix and retry.
                                    setEditedRows((prev) => {
                                        const next = { ...prev };
                                        result.forEach((id) => {
                                            next[id] = false;
                                        });
                                        return next;
                                    });
                                    exitSelectionMode();
                                }
                            });
                        }}
                        disabled={selectedItems.size === 0}
                    >
                        {$t('Confirm ({{count}})', { count: selectedItems.size })}
                    </Button>
                    <Button variant="danger" small onClick={exitSelectionMode}>
                        {$t('Cancel')}
                    </Button>
                </Fragment>
            ) : (
                <Button variant="primary" small onClick={() => enableSelectionMode()} disabled={confirmAllLoading || confirmAllDisabled}>
                    {confirmAllLoading ? $t('Loading…') : $t('Confirm All')}
                </Button>
            )}
        </div>
    );
}

export default function ActualCostsAllocationTable(props) {
    const [editedRows, setEditedRows] = useState({});
    // Tracks the one thing `editedRows` cannot tell apart: whether a row is in
    // edit mode because the RECONCILER asked for it (setEdited, below) or
    // because the table auto-opened it for having nothing allocated (the
    // effect further down, which writes editedRows directly and never touches
    // this map). Only setEdited populates it, so it survives a bulk-apply
    // refetch even when that refetch flips the row's allocations from
    // placeholder to real — see the `manuallyOpenedRow` prop on
    // ActualCostsAllocationRow.
    const [manuallyOpenedRows, setManuallyOpenedRows] = useState({});
    const [collapsedMonths, setCollapsedMonths] = useState({});
    const [columns, setColumns] = useState([]);
    const [displayedColumns, setDisplayedColumns] = useState(defaultVals);

    const [documentTypes, setDocumentTypes] = useState({});
    const [glCodes, setGlCodes] = useState({});
    const [suppliers, setSuppliers] = useState({});
    const [departments, setDepartments] = useState({});
    const [budgetLines, setBudgetLines] = useState({});
    const [contractReferences, setContractReferences] = useState({});

    const [supplierQuery, setSupplierQuery] = useState('');
    const [glCodeQuery, setGlCodeQuery] = useState('');
    const [documentTypeQuery, setDocumentTypeQuery] = useState('');
    const [departmentQuery, setDepartmentQuery] = useState('');
    const [budgetLineQuery, setBudgetLineQuery] = useState('');
    const [contractReferenceQuery, setContractReferenceQuery] = useState('');

    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedItems, setSelectedItems] = useState(new Set());
    const [rowAllocations, setRowAllocations] = useState({});
    const [confirmAllLoading, setConfirmAllLoading] = useState(false);
    const [totalEligibleCount, setTotalEligibleCount] = useState(0);
    const [confirmAllCapped, setConfirmAllCapped] = useState(false);

    // The last bulk apply on THIS table, and so the only one its Undo offers. A
    // second apply supersedes it, exactly as the agreed lifetime says.
    const [bulkApplyResult, setBulkApplyResult] = useState(null);

    const refreshRequest = useApiRequest({
        url: props.refreshUrl,
        successMessage: $t('The refresh has begun, this could take a few minutes depending on the amount of data in the reconciliation'),
        errorMessage: $t('An error ocurred while refreshing transactions'),
    });

    useEffect(() => {
        setColumns(columnsFilter());
        setDisplayedColumns(defaultFilter());
    }, []);

    useEffect(() => {
        combineTableFilters();
    }, [props.tableFilters, props.allContracts]);

    /**
     * Clear every column filter and search box on this table, on the parent's word.
     *
     * The parent owns the filter payload the listing is fetched with, but each
     * column's checkbox state and search box lives HERE — and
     * combineTableFilters() deliberately preserves whatever it already holds
     * (`key in filters ? filters[key] : default`), so a parent-side reset alone
     * would leave the filter icons grey while the boxes behind them still held
     * the old selection, and the next Apply would silently re-send it.
     *
     * A counter rather than a boolean: two clears in a row must both land, and a
     * flag the parent had to unset would need a second round trip to do it.
     */
    useEffect(() => {
        if (!props.clearColumnFiltersSignal) {
            return;
        }

        // Updater form rather than the showAll* helpers, which snapshot their dict
        // from the render closure: the rebuild in combineTableFilters() and this
        // clear must never be able to queue over each other's copy. Handles both
        // filter shapes — a raw boolean per option, or a `{name, show}` object.
        const showEverything = (previous) =>
            Object.fromEntries(
                Object.entries(previous).map(([key, value]) => [key, value !== null && typeof value === 'object' ? { ...value, show: true } : true]),
            );

        setSuppliers(showEverything);
        setGlCodes(showEverything);
        setDocumentTypes(showEverything);
        setDepartments(showEverything);
        setBudgetLines(showEverything);
        setContractReferences(showEverything);

        setSupplierQuery('');
        setGlCodeQuery('');
        setDocumentTypeQuery('');
        setDepartmentQuery('');
        setBudgetLineQuery('');
        setContractReferenceQuery('');
    }, [props.clearColumnFiltersSignal]);

    useEffect(() => {
        if (!displayedColumns.splitAllocation) {
            exitSelectionMode();
        }
    }, [displayedColumns]);

    /**
     * Withdraw the result-bar report on the way out.
     *
     * Switching the type sub-tab (All → Revenue, say) unmounts the other table
     * outright, taking its result bar with it. Without this the page would go on
     * believing that table's bar was up, and would suppress the empty state on the
     * strength of a bar that no longer exists the moment the sub-tab came back.
     *
     * Empty deps deliberately: a cleanup-only effect has no reason to track prop
     * identities, and tracking them would mean an inlined callback at a call site
     * re-ran this on every render — clearing the report from under a live bar.
     */
    useEffect(() => {
        return () => {
            props.onBulkApplyResultChange?.(props.actualCostType, false, !!props.isReconciled);
        };
    }, []);

    const getGlCodeKey = (gCode) => {
        const glCodeObject = gCode === EMPTY_KEY ? null : props.glCodes[gCode];

        return glCodeObject ? glCodeObject.code + ' - ' + glCodeObject.name : gCode;
    };

    const deleteInvalidKeys = (obj, keys) => {
        const validObj = JSON.parse(JSON.stringify(obj));

        Object.keys(validObj).forEach((key) => {
            if (!keys.includes(key)) {
                delete validObj[key];
            }
        });

        return validObj;
    };

    // Every budget line the payload knows about, keyed by typed id, regardless of
    // which allocatable or Workforce group it arrived under. The filter rebuild
    // below narrows its search to the departments currently in play, which misses
    // a legacy template / pack child whose owning department is not among them —
    // and an unresolved id collapses into the "Empty" bucket, which would hide the
    // very allocations the legacy marker exists to surface.
    const allLineItemsByTypedId = useMemo(() => {
        const lookup = new Map();

        Object.values(props.directLineItems ?? {}).forEach((groups) => {
            Object.values(groups ?? {}).forEach((lineItems) => {
                (lineItems ?? []).forEach((lineItem) => {
                    if (lineItem?.typed_id && !lookup.has(lineItem.typed_id)) {
                        lookup.set(lineItem.typed_id, lineItem);
                    }
                });
            });
        });

        return lookup;
    }, [props.directLineItems]);

    const budgetLineFilterName = (lineItem) => (lineItem.is_legacy_child ? $t('{{name}} (legacy)', { name: lineItem.name }) : lineItem.name);

    const combineTableFilters = () => {
        let documentTypesFilters = JSON.parse(JSON.stringify(documentTypes));
        let glCodesFilters = JSON.parse(JSON.stringify(glCodes));
        let suppliersFilters = JSON.parse(JSON.stringify(suppliers));
        let departmentsFilters = JSON.parse(JSON.stringify(departments));
        let blFilters = JSON.parse(JSON.stringify(budgetLines));
        let contractFilters = JSON.parse(JSON.stringify(contractReferences));

        const type = props.actualCostType;

        if (props.tableFilters.suppliers !== undefined) {
            const validSKeys = [];
            props.tableFilters.suppliers.forEach((supplier) => {
                const sName = supplier || EMPTY_KEY;
                suppliersFilters[sName] = sName in suppliersFilters ? suppliersFilters[sName] : true;
                validSKeys.push(sName);
            });

            suppliersFilters = deleteInvalidKeys(suppliersFilters, validSKeys);
        }
        if (props.tableFilters.glCodes !== undefined) {
            const validGKeys = [];
            props.tableFilters.glCodes.forEach((glCode) => {
                const gCode = glCode || EMPTY_KEY;
                const glCodeKey = getGlCodeKey(gCode);
                glCodesFilters[glCodeKey] = glCodeKey in glCodesFilters ? glCodesFilters[glCodeKey] : true;
                validGKeys.push(glCodeKey);
            });

            glCodesFilters = deleteInvalidKeys(glCodesFilters, validGKeys);
        }
        if (props.tableFilters.documentTypes !== undefined) {
            const validDtKeys = [];
            props.tableFilters.documentTypes.forEach((documentType) => {
                const dType = documentType || EMPTY_KEY;
                documentTypesFilters[dType] = dType in documentTypesFilters ? documentTypesFilters[dType] : true;
                validDtKeys.push(dType);
            });

            documentTypesFilters = deleteInvalidKeys(documentTypesFilters, validDtKeys);
        }
        if (props.tableFilters.departmentsCostcenters !== undefined) {
            const validDepKeys = [];
            props.tableFilters.departmentsCostcenters.forEach((typedAllocatableId) => {
                const departmentsList = props.departments[type] ?? [];
                const dep = departmentsList.find((d) => d.typed_id === typedAllocatableId);
                if (dep) {
                    departmentsFilters[typedAllocatableId] =
                        typedAllocatableId in departmentsFilters ? departmentsFilters[typedAllocatableId] : { name: dep.name, show: true };
                    validDepKeys.push(typedAllocatableId);
                } else {
                    departmentsFilters[EMPTY_KEY] = EMPTY_KEY in departmentsFilters ? departmentsFilters[EMPTY_KEY] : { name: EMPTY_KEY, show: true };
                    validDepKeys.push(EMPTY_KEY);
                }
            });

            departmentsFilters = deleteInvalidKeys(departmentsFilters, validDepKeys);
        }
        if (props.tableFilters.budgetLines !== undefined) {
            const validLiKeys = [];
            let directLineItemsList = [];
            Object.keys(props.directLineItems).forEach((allId) => {
                if (props.tableFilters.departmentsCostcenters.includes(allId)) {
                    const depDirectLineItemsList = props.directLineItems[allId] ?? {};
                    Object.keys(depDirectLineItemsList).forEach((group) => {
                        directLineItemsList = [...directLineItemsList, ...depDirectLineItemsList[group]];
                    });
                }
            });

            props.tableFilters.budgetLines.forEach((typedValuableId) => {
                if (typedValuableId) {
                    const bl = directLineItemsList.find((li) => li.typed_id === typedValuableId) ?? allLineItemsByTypedId.get(typedValuableId);
                    if (bl) {
                        let skip = false;
                        if (typedValuableId.includes('total_workforce') && Object.keys(blFilters).some((key) => key.includes('total_workforce'))) {
                            skip = true;
                        }
                        if (!skip) {
                            blFilters[typedValuableId] =
                                typedValuableId in blFilters ? blFilters[typedValuableId] : { name: budgetLineFilterName(bl), show: true };
                            validLiKeys.push(typedValuableId);
                        }
                    } else {
                        blFilters[EMPTY_KEY] = EMPTY_KEY in blFilters ? blFilters[EMPTY_KEY] : { name: EMPTY_KEY, show: true };
                        validLiKeys.push(EMPTY_KEY);
                    }
                } else {
                    blFilters[EMPTY_KEY] = EMPTY_KEY in blFilters ? blFilters[EMPTY_KEY] : { name: EMPTY_KEY, show: true };
                    validLiKeys.push(EMPTY_KEY);
                }
            });

            blFilters = deleteInvalidKeys(blFilters, validLiKeys);
        }

        if (props.tableFilters.contractReferences !== undefined) {
            const validCrKeys = [];
            let contracts = [];

            Object.keys(props.allContracts).forEach((cType) => {
                const contractsList = props.allContracts[cType] ?? {};
                Object.keys(contractsList).forEach((group) => {
                    contracts = [...contracts, ...contractsList[group]];
                });
            });

            props.tableFilters.contractReferences.forEach((typedContractableId) => {
                if (typedContractableId) {
                    const contractData = contracts.find((c) => c.typed_id === typedContractableId);
                    if (contractData) {
                        contractFilters[typedContractableId] =
                            typedContractableId in contractFilters ? contractFilters[typedContractableId] : { name: contractData.reference, show: true };
                        validCrKeys.push(typedContractableId);
                    } else {
                        contractFilters[EMPTY_KEY] = EMPTY_KEY in contractFilters ? contractFilters[EMPTY_KEY] : { name: EMPTY_KEY, show: true };
                        validCrKeys.push(EMPTY_KEY);
                    }
                } else {
                    contractFilters[EMPTY_KEY] = EMPTY_KEY in contractFilters ? contractFilters[EMPTY_KEY] : { name: EMPTY_KEY, show: true };
                    validCrKeys.push(EMPTY_KEY);
                }
            });
            contractFilters = deleteInvalidKeys(contractFilters, validCrKeys);
        }

        setDocumentTypes(sortSimpleFilterObject(documentTypesFilters));
        setGlCodes(sortSimpleFilterObject(glCodesFilters));
        setSuppliers(sortSimpleFilterObject(suppliersFilters));
        setDepartments(sortFiltersByName(departmentsFilters));
        setBudgetLines(sortFiltersByName(blFilters));
        setContractReferences(sortFiltersByName(contractFilters));
    };

    const collectMatchingIds = (type, query, source) => {
        const lowerQuery = query.toLowerCase();
        const ids = [];

        if (type === 'department') {
            source.forEach((item) => {
                if (item.name.toLowerCase().includes(lowerQuery)) {
                    ids.push(item.typed_id);
                }
            });
        } else {
            const labelKey = type === 'contractReference' ? 'reference' : 'name';
            Object.values(source).forEach((allocData) => {
                Object.values(allocData).forEach((group) => {
                    group.forEach((item) => {
                        if (item[labelKey].toLowerCase().includes(lowerQuery)) {
                            ids.push(item.typed_id);
                        }
                    });
                });
            });
        }

        return ids;
    };

    const prePopulationKeyMap = {
        department: 'typed_allocatable_id',
        budgetLine: 'typed_valuable_id',
        contractReference: 'typed_contractable_id',
    };

    const searchedByAllocations = (type, query, row, source) => {
        const costAllocations = row.costAllocations;
        const hasAllocations = costAllocations && costAllocations.length > 0;

        if (!hasAllocations) {
            const prePopulation = row.defaultObject;
            if (!prePopulation) return false;

            const suggested = prePopulation[prePopulationKeyMap[type]];
            if (!suggested) return false;

            return collectMatchingIds(type, query, source).includes(suggested);
        }

        let display = false;

        if (type === 'department') {
            const ids = [];
            source.forEach((item) => {
                if (item.name.toLowerCase().includes(query.toLowerCase())) {
                    ids.push(item.typed_id);
                }
            });

            costAllocations.forEach((item) => {
                if (ids.includes(item.typed_allocatable_id)) {
                    display = true;
                }
            });
        }

        if (type === 'contractReference') {
            const ids = [];

            Object.keys(source).forEach((alloc) => {
                const allocData = source[alloc];
                Object.keys(allocData).forEach((group) => {
                    allocData[group].forEach((item) => {
                        if (item.reference.toLowerCase().includes(query.toLowerCase())) {
                            ids.push(item.typed_id);
                        }
                    });
                });
            });

            costAllocations.forEach((item) => {
                if (ids.includes(item.typed_contractable_id)) {
                    display = true;
                }
            });
        }

        if (type === 'budgetLine') {
            const ids = [];

            Object.keys(source).forEach((alloc) => {
                const allocData = source[alloc];
                Object.keys(allocData).forEach((group) => {
                    allocData[group].forEach((item) => {
                        if (item.name.toLowerCase().includes(query.toLowerCase())) {
                            ids.push(item.typed_id);
                        }
                    });
                });
            });

            costAllocations.forEach((item) => {
                if (ids.includes(item.typed_valuable_id)) {
                    display = true;
                }
            });
        }

        return display;
    };

    const isFilterActive = (type) => {
        if (type === 'suppliers' || type === 'glCodes' || type === 'documentTypes') {
            const datum =
                type === 'suppliers' ? props.filters.filters.supplier : type === 'glCodes' ? props.filters.filters.glCode : props.filters.filters.documentType;
            let active = false;
            Object.values(datum).forEach((val) => {
                if (!val) {
                    active = true;
                }
            });

            return active;
        }

        if (type === 'departments' || type === 'budgetLines' || type === 'contractReferences') {
            const datum =
                type === 'departments'
                    ? props.filters.filters.department
                    : type === 'budgetLines'
                      ? props.filters.filters.budgetLine
                      : props.filters.filters.contractReference;

            return Object.values(datum).some((item) => item.show === false);
        }

        return false;
    };

    // Any header filter or search narrows what the page returns; the month
    // aggregate is unaffected by them, so the badge switches to its "(n shown)"
    // form while one is on.
    const columnFilterActive = COLUMN_FILTER_TYPES.some((type) => isFilterActive(type)) || Object.values(props.filters?.search ?? {}).some((value) => !!value);

    const monthCounts = props.monthCounts?.[props.actualCostType] ?? {};

    /**
     * Reconcile the unsaved in-memory allocations of rows a bulk apply (or its
     * undo) has just rewritten server-side.
     *
     * A cached in-memory allocation wins over the fetched row (see
     * `cachedAllocations` in ActualCostsAllocationRow), so leaving it untouched
     * would show the reconciler a stale value and later save it over what the bulk
     * action wrote. Dropping the whole entry fixes that but throws away unsaved
     * edits to the row's OTHER columns — most visibly on the source row, which is
     * usually mid-edit. So only the applied column is overwritten; everything else
     * the user typed survives.
     */
    const reconcileInMemoryEdits = (ids, column, value) => {
        if (!ids?.length) {
            return;
        }

        const field = BULK_APPLY_FIELDS[column];

        setRowAllocations((previous) => {
            const next = { ...previous };

            ids.forEach((id) => {
                const cached = next[id];

                if (!cached?.length) {
                    return;
                }

                // An undo passes no column: it restored each row to a value this side
                // never knew, so the cached copy has to go entirely.
                if (!field) {
                    delete next[id];

                    return;
                }

                // Only the first allocation: a row the server wrote to had exactly one
                // persisted allocation (split rows are never targets), so that is the
                // one the applied value landed on. A split the user added in memory but
                // has not saved is left as they left it.
                next[id] = cached.map((allocation, index) => (index === 0 ? { ...allocation, [field]: value } : allocation));
            });

            return next;
        });
    };

    /**
     * Which column the bulk-apply panel's "rows matching" selector opens on.
     *
     * The agreed default is the most recently applied filter, tracked for this
     * visit only; where that is not one of the three matchable columns, it falls
     * back to whichever of them is active, and then to the first by column order.
     * Note the filter keys are singular while isFilterActive() takes the plural
     * type names.
     */
    const defaultMatchColumn = useMemo(() => {
        const matchable = [
            { filterKey: 'supplier', filterType: 'suppliers', column: 'supplier' },
            { filterKey: 'glCode', filterType: 'glCodes', column: 'gl_code' },
            { filterKey: 'documentType', filterType: 'documentTypes', column: 'document_type' },
        ];

        const touched = matchable.find((option) => option.filterKey === props.lastTouchedFilter);
        if (touched) {
            return touched.column;
        }

        const active = matchable.find((option) => isFilterActive(option.filterType) || !!props.filters?.search?.[option.filterKey]);

        return (active ?? matchable[0]).column;
    }, [props.lastTouchedFilter, props.filters]);

    // Both tables can be bulk applied to, but not with the same columns — see
    // BULK_APPLY_COLUMNS_RECONCILED. The reconciled table's use for it is the pass
    // that cannot be done before a row reconciles: a contract reference across rows
    // that have already left the unreconciled list.
    const bulkApply = useMemo(
        () => ({
            enabled: !!props.bulkApplyUrls?.preview,
            columns: props.isReconciled ? BULK_APPLY_COLUMNS_RECONCILED : BULK_APPLY_COLUMNS,
            available: columnFilterActive,
            filters: props.filters,
            actualCostType: props.actualCostType,
            isReconciled: !!props.isReconciled,
            urls: props.bulkApplyUrls,
            defaultMatchColumn,
            onApplied: (result) => {
                setBulkApplyResult(result);
                reconcileInMemoryEdits(result.applied ?? [], result.column, result.value);
                // The bar lives in this component, so the page has to be told it is
                // up: while it is, the page must keep this table mounted or the bar
                // — and the only Undo the action has — goes with it.
                props.onBulkApplyResultChange?.(props.actualCostType, true, !!props.isReconciled);
                // The result travels up as well as into the bar: rows that
                // reconciled have left this list, and the page decides from that
                // count whether the filter is now hiding everything.
                props.onBulkApplied?.(props.actualCostType, result);
            },
        }),
        [
            props.isReconciled,
            props.bulkApplyUrls,
            columnFilterActive,
            props.filters,
            props.actualCostType,
            defaultMatchColumn,
            props.onBulkApplied,
            props.onBulkApplyResultChange,
        ],
    );

    // Announce bulk apply the moment it becomes available, since its trigger only
    // shows on hover. Keyed off the same `bulkApply` flags the trigger uses, so the
    // hint can never advertise something the user cannot then do.
    //
    // `props.collapsed` reads backwards: it is TRUE while the table is expanded
    // (see the table wrapper's `!props.collapsed && '!tw-hidden'` below, and the
    // +/- button above). Aliased here so the gate reads naturally rather than
    // asking the reader to hold the inversion in their head at the use site.
    const isExpanded = !!props.collapsed;

    const showBulkApplyHint =
        bulkApply.enabled &&
        bulkApply.available &&
        !props.bulkApplyHintDismissed &&
        isExpanded &&
        // Mid-refetch the totals still describe the previous filter, so the hint
        // would appear announcing the pre-filter row count and then correct itself.
        !props.request &&
        // A result bar means this user has already found the feature, and two
        // full-width alerts stacked on one table reads badly.
        !bulkApplyResult &&
        // Nothing to apply *across* — and "all 1 rows" does not read.
        (props.pagination?.total ?? 0) >= 2;

    const sortSimpleFilterObject = (obj) => {
        const sortedKeys = Object.keys(obj).sort();
        const sortedObj = {};
        for (const key of sortedKeys) {
            sortedObj[key] = obj[key];
        }

        return sortedObj;
    };

    const sortFiltersByName = (obj) => {
        return Object.fromEntries(Object.entries(obj).sort(([, a], [, b]) => a.name.localeCompare(b.name)));
    };

    const columnsFilter = () => {
        const result = [
            { documentType: $t('Document Type') },
            { documentNumber: $t('Document Reference') },
            { transactionDate: $t('Transaction Date') },
            { glCode: $t('G/L Code') },
        ];

        if (props.taxEnabled) {
            result.push({ totalExcludeTax: $t('Total Exc. Tax') });
            result.push({ tax: $t('Tax') });
            result.push({ total: $t('Total') });
            result.push({ taxExcluded: $t('Tax Excluded') });
            result.push({ taxRate: $t('Tax Rate') });
        } else {
            result.push({ rawAmount: $t('Amount') });
        }

        result.push({ type: $t('Type') });
        result.push({ transactionType: $t('Transaction') });
        result.push({ department: $t('Department / Cost Centre') });
        result.push({ budgetLine: $t('Budget Line') });
        result.push({ contractReference: $t('Contract Reference') });
        result.push({ amount: $t('Amount') });
        result.push({ splitAllocation: $t('Split Allocation') });

        return result;
    };

    const defaultFilter = () => {
        const stored = localStorage.getItem(props.storeKey);
        if (stored) {
            return JSON.parse(stored);
        }

        const cols = columnsFilter();
        const res = {};
        cols.forEach((col) => {
            const key = Object.keys(col)[0];
            res[key] = defaultVals[key] ?? true;
        });

        return res;
    };

    useEffect(() => {
        // Rebuild from the rows currently in the table: keep the in-progress
        // edit state of rows that are still present, but drop entries for rows
        // that left (e.g. moved to the other reconciliation tab) so a returning
        // row re-derives its state from its allocations instead of inheriting a
        // stale edit flag.
        const editedData = {};
        const collapsed = JSON.parse(JSON.stringify(collapsedMonths));

        Object.keys(props.data).forEach((month, key) => {
            if (!(month in collapsed)) {
                collapsed[month] = true;
            }

            props.data[month].forEach((actualCosts, rowIndex) => {
                editedData[actualCosts.id] = editedRows[actualCosts.id] ?? actualCosts.costAllocations.length === 0;
            });
        });

        setCollapsedMonths(collapsed);

        setEditedRows(editedData);
    }, [props.data]);

    const toggleCollapsed = (month) => {
        const collapsed = JSON.parse(JSON.stringify(collapsedMonths));
        if (month in collapsed) {
            collapsed[month] = !collapsed[month];
        }

        setCollapsedMonths(collapsed);
    };

    const formatMonth = (month) => {
        const date = new Date(month + '-01');

        // Follow the app locale (set as CURRENT_LOCALE by the layout) rather than a
        // hardcoded en-US, falling back to the browser's when it is not available.
        const locale = typeof window.CURRENT_LOCALE === 'string' ? window.CURRENT_LOCALE.replace('_', '-') : undefined;

        return date.toLocaleDateString(locale, {
            month: 'long',
            year: 'numeric',
        });
    };

    const setEdited = (id, value = true) => {
        const editedData = JSON.parse(JSON.stringify(editedRows));
        editedData[id] = value;
        setEditedRows(editedData);

        setManuallyOpenedRows((previous) => ({ ...previous, [id]: value }));
    };

    /**
     * Hand a row to the parent's archive flow, first dropping any inline edit it
     * has open.
     *
     * Archiving is a deliberate decision about that specific row, so an unsaved
     * allocation edit on it is discarded rather than defended with a second
     * prompt. Clearing the cached allocations matters as much as leaving edit
     * mode: a stale entry would be re-applied to whatever row later takes that id
     * slot on screen, and would follow the row into a Confirm All payload.
     */
    const archiveActualCost = (actualCost) => {
        if (!props.archiveActualCost) {
            return;
        }

        setEdited(actualCost.id, false);
        setRowAllocations((previous) => {
            const next = { ...previous };
            delete next[actualCost.id];
            return next;
        });
        setSelectedItems((previous) => new Set([...previous].filter((id) => id !== actualCost.id)));

        props.archiveActualCost(actualCost);
    };

    const hideAllColumns = () => {
        const options = JSON.parse(JSON.stringify(displayedColumns));
        Object.keys(displayedColumns).forEach((key) => {
            options[key] = false;
        });
        setDisplayedColumns(options);
        localStorage.setItem(props.storeKey, JSON.stringify(options));
    };
    const showAllColumns = () => {
        const options = JSON.parse(JSON.stringify(displayedColumns));
        Object.keys(displayedColumns).forEach((key) => {
            options[key] = true;
        });
        setDisplayedColumns(options);
        localStorage.setItem(props.storeKey, JSON.stringify(options));
    };

    const toggleColumn = (key) => {
        const options = JSON.parse(JSON.stringify(displayedColumns));
        options[key] = !options[key];
        setDisplayedColumns(options);
        localStorage.setItem(props.storeKey, JSON.stringify(options));
    };

    const colspanVal = (full = true) => {
        let res = 0;
        Object.keys(displayedColumns).forEach((key) => {
            const val = displayedColumns[key];
            if (val) {
                res += 1;
            }
        });

        return res;
    };

    const getContracts = (item) => {
        if (!item.supplier_id) {
            return [];
        }

        const supplier = props.suppliers.find((row) => {
            return row.id === item.supplier_id;
        });

        return supplier?.contracts ?? [];
    };

    const displayByContractReference = (actualCost) => {
        const allocations = actualCost.costAllocations ?? [];

        if (allocations.length === 0) {
            // No manual allocations — check pre_population suggestion for this column.
            const suggested = actualCost.defaultObject?.typed_contractable_id;
            if (suggested) {
                const filterData = contractReferences[suggested] ?? { show: true };
                return filterData.show;
            }
            // No suggestion either — falls into the Empty bucket.
            return contractReferences[EMPTY_KEY] ? contractReferences[EMPTY_KEY].show : true;
        }

        let show = false;
        allocations.forEach((allocation) => {
            const typedContractableId = allocation.typed_contractable_id ?? EMPTY_KEY;
            const filterData = contractReferences[typedContractableId] ??
                contractReferences[EMPTY_KEY] ?? {
                    show: true,
                };

            if (filterData.show) {
                show = show || filterData.show;
            }
        });

        return show;
    };

    const showAllContractReferences = (val = true) => {
        const datum = JSON.parse(JSON.stringify(contractReferences));
        Object.keys(datum).forEach((key) => {
            datum[key].show = val;
        });

        setContractReferences(datum);
    };

    const toggleContractReference = (key) => {
        const datum = JSON.parse(JSON.stringify(contractReferences));
        datum[key].show = !datum[key].show;

        setContractReferences(datum);
    };

    const displayByBudgetLines = (actualCost) => {
        const allocations = actualCost.costAllocations ?? [];

        if (allocations.length === 0) {
            // No manual allocations — check pre_population suggestion for this column.
            const suggested = actualCost.defaultObject?.typed_valuable_id;
            if (suggested) {
                const filterData = budgetLines[suggested] ?? { show: true };
                return filterData.show;
            }
            // No suggestion either — falls into the Empty bucket.
            return budgetLines[EMPTY_KEY] ? budgetLines[EMPTY_KEY].show : true;
        }

        let show = false;
        allocations.forEach((allocation) => {
            const typedValuableId = allocation.typed_valuable_id ?? EMPTY_KEY;
            const filterData = budgetLines[typedValuableId] ??
                budgetLines[EMPTY_KEY] ?? {
                    show: true,
                };

            if (filterData.show) {
                show = show || filterData.show;
            }
        });

        return show;
    };

    const showAllBudgetLines = (val = true) => {
        const datum = JSON.parse(JSON.stringify(budgetLines));
        Object.keys(datum).forEach((key) => {
            datum[key].show = val;
        });

        setBudgetLines(datum);
    };

    const toggleBudgetLine = (key) => {
        const datum = JSON.parse(JSON.stringify(budgetLines));
        datum[key].show = !datum[key].show;

        setBudgetLines(datum);
    };

    const displayByDepartment = (actualCost) => {
        const allocations = actualCost.costAllocations ?? [];

        if (allocations.length === 0) {
            // No manual allocations — check pre_population suggestion for this column.
            const suggested = actualCost.defaultObject?.typed_allocatable_id;
            if (suggested) {
                const filterData = departments[suggested] ?? { show: true };
                return filterData.show;
            }
            // No suggestion either — falls into the Empty bucket.
            return departments[EMPTY_KEY] ? departments[EMPTY_KEY].show : true;
        }

        let show = false;
        allocations.forEach((allocation) => {
            const typedAllocatableId = allocation.typed_allocatable_id ?? EMPTY_KEY;
            const filterData = departments[typedAllocatableId] ??
                departments[EMPTY_KEY] ?? {
                    show: true,
                };

            if (filterData.show) {
                show = show || filterData.show;
            }
        });

        return show;
    };

    const showAllDepartments = (val = true) => {
        const datum = JSON.parse(JSON.stringify(departments));
        Object.keys(datum).forEach((key) => {
            datum[key].show = val;
        });

        setDepartments(datum);
    };

    const toggleDepartment = (key) => {
        const datum = JSON.parse(JSON.stringify(departments));
        datum[key].show = !datum[key].show;

        setDepartments(datum);
    };

    const showAllSuppliers = (val = true) => {
        const datum = JSON.parse(JSON.stringify(suppliers));
        Object.keys(datum).forEach((key) => {
            datum[key] = val;
        });

        setSuppliers(datum);
    };

    const toggleSupplier = (key) => {
        const datum = JSON.parse(JSON.stringify(suppliers));
        datum[key] = !datum[key];

        setSuppliers(datum);
    };

    const showAllDocumentTypes = (val = true) => {
        const datum = JSON.parse(JSON.stringify(documentTypes));
        Object.keys(datum).forEach((key) => {
            datum[key] = val;
        });

        setDocumentTypes(datum);
    };

    const toggleDocumentType = (key) => {
        const datum = JSON.parse(JSON.stringify(documentTypes));
        datum[key] = !datum[key];

        setDocumentTypes(datum);
    };

    const showAllGlCodes = (val = true) => {
        const datum = JSON.parse(JSON.stringify(glCodes));
        Object.keys(datum).forEach((key) => {
            datum[key] = val;
        });

        setGlCodes(datum);
    };

    const toggleGlCode = (key) => {
        const datum = JSON.parse(JSON.stringify(glCodes));
        datum[key] = !datum[key];

        setGlCodes(datum);
    };

    const refreshPrePopulation = () => {
        if (props.refreshUrl) {
            refreshRequest.get();
        }
    };

    // NOTE: This mirrors ActualCostsService::areAllocationsEligibleForConfirmation on the backend.
    // Keep both in sync when eligibility rules change.
    const areAllocationsEligible = (allocations) => {
        if (!allocations || allocations.length === 0) {
            return false;
        }

        return allocations.every((a) => a.typed_allocatable_id && a.typed_valuable_id && a.type && a.transaction_type);
    };

    // Each row reports its in-memory allocations as the user edits. The table uses these to
    // drive live Confirm All eligibility AND to build the bulk-confirm payload, so the backend
    // receives the same allocation data as a single-row save (saveAllocations).
    const reportRowAllocations = useCallback((id, allocations) => {
        setRowAllocations((prev) => ({ ...prev, [id]: allocations }));
    }, []);

    const getAllocationsForId = (id, fallbackCostAllocations) => {
        return rowAllocations[id] ?? fallbackCostAllocations ?? [];
    };

    const eligibleIds = useMemo(() => {
        const eligible = new Set();
        Object.keys(props.data).forEach((month) => {
            props.data[month].forEach((actualCost) => {
                // On the reconciled table every row already has complete persisted
                // allocations, so only rows with unsaved in-memory edits are
                // confirmable — otherwise Confirm All would re-confirm everything.
                if (props.isReconciled && rowAllocations[actualCost.id] === undefined) {
                    return;
                }
                const allocations = getAllocationsForId(actualCost.id, actualCost.costAllocations);
                if (areAllocationsEligible(allocations)) {
                    eligible.add(actualCost.id);
                }
            });
        });
        return eligible;
    }, [props.data, rowAllocations, props.isReconciled]);

    const onScreenIds = useMemo(() => {
        const ids = new Set();
        Object.keys(props.data).forEach((month) => {
            props.data[month].forEach((actualCost) => {
                ids.add(actualCost.id);
            });
        });
        return ids;
    }, [props.data]);

    // IDs that have been edited in-memory (across any page) and are eligible.
    // These may not be in props.data (off-screen pages) but should still be
    // included in the confirm-all selection so the user's unsaved edits are sent.
    const offPageEligibleIds = useMemo(() => {
        const eligible = new Set();

        Object.keys(rowAllocations).forEach((id) => {
            const numId = Number(id);
            if (!onScreenIds.has(numId) && !onScreenIds.has(id)) {
                if (areAllocationsEligible(rowAllocations[id])) {
                    eligible.add(numId);
                }
            }
        });
        return eligible;
    }, [onScreenIds, rowAllocations]);

    // On the reconciled table only edited rows are confirmable, so disable the
    // button until at least one row (on-screen or off-page) has in-memory edits.
    // On the unreconciled table off-screen rows may be eligible via persisted
    // allocations, so any rows at all keep the button enabled.
    const confirmAllDisabled = props.isReconciled
        ? eligibleIds.size === 0 && offPageEligibleIds.size === 0
        : props.pagination.total === 0 && eligibleIds.size === 0;

    const exitSelectionMode = () => {
        setIsSelectionMode(false);
        setSelectedItems(new Set());
        setTotalEligibleCount(0);
        setConfirmAllCapped(false);
    };

    const enableSelectionMode = async () => {
        if (!props.eligibleIdsUrl) {
            const allEligible = new Set([...eligibleIds, ...offPageEligibleIds]);
            setSelectedItems(allEligible);
            setTotalEligibleCount(allEligible.size);
            setIsSelectionMode(true);
            return;
        }

        setConfirmAllLoading(true);
        try {
            // Send the off-page in-memory edited IDs so the backend can return
            // only those that match the active header filters. Otherwise unsaved
            // edits to rows that no longer match (e.g. excluded by Search Budget
            // Line / Budget Lines selection) would leak into Confirm All.
            const inMemoryIds = [...offPageEligibleIds];
            const res = await axios.post(props.eligibleIdsUrl, {
                type: props.actualCostType,
                filtersAndSearch: props.filters,
                inMemoryIds,
                reconciled: !!props.isReconciled,
            });
            const { ids = [], capped = false, matchingInMemoryIds = [] } = res.data ?? {};

            const filteredOffPageEligible = new Set(matchingInMemoryIds.filter((id) => offPageEligibleIds.has(id)));

            const filteredBackendIds = ids.filter((id) => !onScreenIds.has(id) || eligibleIds.has(id));

            if (filteredBackendIds.length === 0 && eligibleIds.size === 0 && filteredOffPageEligible.size === 0) {
                props.toast.info?.($t('No eligible items to confirm.'));
                return;
            }

            // Merge IDs from the backend (off-screen items with persisted/pre-populated
            // allocations), the current page (in-memory allocations checked by
            // areAllocationsEligible), and off-page rows whose in-memory edits are
            // eligible AND match the active filter.
            const merged = new Set([...filteredBackendIds, ...eligibleIds, ...filteredOffPageEligible]);
            setSelectedItems(merged);
            setTotalEligibleCount(merged.size);
            setConfirmAllCapped(capped);
            setIsSelectionMode(true);
        } catch (e) {
            props.toast.error($t('Could not load items to confirm.'));
        } finally {
            setConfirmAllLoading(false);
        }
    };

    const buildConfirmPayload = () => {
        const lookup = {};
        Object.keys(props.data).forEach((month) => {
            props.data[month].forEach((actualCost) => {
                lookup[actualCost.id] = actualCost;
            });
        });

        const pending = [];
        const ids = [];
        selectedItems.forEach((id) => {
            if (lookup[id]) {
                // On-screen row: send with its current in-memory allocations (or persisted
                // fallback) so any unsaved edits are included in the confirmation.
                pending.push({
                    id,
                    allocations: getAllocationsForId(id, lookup[id].costAllocations),
                });
            } else if (rowAllocations[id] !== undefined) {
                // Off-screen row with in-memory edits: send as pending so the user's
                // unsaved allocation changes are applied, not silently ignored.
                pending.push({
                    id,
                    allocations: rowAllocations[id],
                });
            } else {
                // Off-screen row with no in-memory edits: let the backend use its
                // persisted allocations.
                ids.push(id);
            }
        });

        return { pending, ids };
    };

    useEffect(() => {
        if (isSelectionMode) {
            exitSelectionMode();
        }
    }, [props.filters, props.dateFilter, props.glCodeFilter]);

    return (
        <div className="tw-mb-3">
            <div className="tw-flex tw-justify-between tw-items-start">
                <div className="tw-flex tw-items-center tw-gap-x-1.5 tw-mb-4">
                    <h3 className="tw-my-3">{props.header}</h3>
                    {!!props.refreshUrl && (
                        <button className="table__icon-btn tw-text-white" onClick={refreshPrePopulation}>
                            <RefreshSync className="tw-w-3.5 tw-h-3.5" />
                        </button>
                    )}
                </div>

                <div className="tw-flex tw-gap-x-1.5">
                    <div>
                        <TableFilterModal
                            id="project-plan-table_filter-modal"
                            onCancel={hideAllColumns}
                            showCancel={true}
                            onApply={showAllColumns}
                            cancelText={$t('Hide all')}
                            applyText={$t('Show all')}
                            headerText={$t('Displayed Columns')}
                        >
                            <div>
                                <div className="tw-grid tw-grid-cols-4 tw-gap-4">
                                    {columns.map((col, index) => {
                                        const key = Object.keys(col)[0];
                                        const value = col[key];

                                        const checked = !!displayedColumns[key];

                                        return (
                                            <div key={key}>
                                                <CheckboxInput label={value} checked={checked} onChange={() => toggleColumn(key)} />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </TableFilterModal>
                    </div>
                    <button className="table__icon-btn tw-text-white" onClick={props.toggleCollapsed}>
                        {!props.collapsed && <PlusIcon className="tw-w-3.5 tw-h-3.5" />}
                        {props.collapsed && <MinusIcon className="tw-w-3.5 tw-h-3.5" />}
                    </button>
                </div>
            </div>
            {isSelectionMode && confirmAllCapped && (
                <div className="tw-mb-2 tw-p-2 tw-text-xs tw-bg-yellow-100 tw-border tw-border-yellow-300 tw-rounded">
                    {$t('Showing the first 5000 eligible items. Confirm them, then run Confirm All again to process the rest.')}
                </div>
            )}
            {showBulkApplyHint && <BulkApplyHint count={props.pagination.total} contractOnly={!!props.isReconciled} onDismiss={props.onDismissBulkApplyHint} />}
            {!!bulkApplyResult && (
                <BulkApplyResultBar
                    result={bulkApplyResult}
                    undoUrl={props.bulkApplyUrls?.undo}
                    onUndone={(reverted) => {
                        setBulkApplyResult(null);
                        props.onBulkApplyResultChange?.(props.actualCostType, false, !!props.isReconciled);
                        reconcileInMemoryEdits(reverted, null, null);
                        props.onBulkApplied?.(props.actualCostType);
                    }}
                    onDismiss={() => {
                        setBulkApplyResult(null);
                        props.onBulkApplyResultChange?.(props.actualCostType, false, !!props.isReconciled);
                    }}
                    toast={props.toast}
                />
            )}
            <div className="table__pagination-info">
                <PaginationInfo to={props.pagination.to} from={props.pagination.from} total={props.pagination.total} />
                <TablePagination
                    lastPage={props.pagination.lastPage}
                    showPerPage={true}
                    perPage={props.pagination.perPage}
                    currentPage={props.pagination.page}
                    perPageOptions={[10, 20, 40, 50, 100, 200]}
                    onPerPageChange={(val) => {
                        props.setPaginationPerPage(val);
                    }}
                    pageChange={(data) => {
                        props.setPaginationPage(+data.selected + 1);
                    }}
                />
            </div>
            <div className={classNames('inbud-table_wrapper', 'tw-overflow-auto', 'max-h-50vh', 'tw-relative', !props.collapsed && '!tw-hidden')}>
                <table className="reconciliation-table inbud-table inbud-table_first-two-sticky-80" style={{ width: 'max-content' }}>
                    <thead className="tw-sticky tw-top-0" style={{ zIndex: 15 }}>
                        <tr>
                            <th className="tw-w-80">{$t('Description')}</th>
                            <th className="tw-w-80">
                                <div className="tw-flex tw-justify-center tw-items-baseline">
                                    <div>{$t('Supplier')}</div>
                                    <div className="tw-ml-2">
                                        <TableFilterModalThreeHandlers
                                            id={`${props.storeKey}_suppliers_filter-modal`}
                                            headerText={$t('Displayed Suppliers')}
                                            customIconStyle={'small'}
                                            onApply={() => {
                                                props.setFilters('supplier', suppliers, true);
                                            }}
                                            applyText={$t('Apply')}
                                            onProcess={showAllSuppliers}
                                            processText={$t('Show all')}
                                            showProcess={true}
                                            onCancel={() => showAllSuppliers(false)}
                                            showCancel={true}
                                            cancelText={$t('Hide all')}
                                            closeOnAction={false}
                                            contentMaxHeight={'30vh'}
                                            isActive={isFilterActive('suppliers')}
                                        >
                                            <div>
                                                <div className="tw-grid tw-grid-cols-3 tw-gap-3">
                                                    {Object.keys(suppliers).map((key) => {
                                                        const checked = !!suppliers[key];

                                                        return (
                                                            <div key={key}>
                                                                <CheckboxInput label={key} checked={checked} onChange={() => toggleSupplier(key)} />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </TableFilterModalThreeHandlers>
                                    </div>
                                    <div className="tw-pl-2">
                                        <TableSearchModal
                                            id={`${props.storeKey}_suppliers_search-modal`}
                                            headerText={$t('Search Suppliers')}
                                            placeholder={$t('Type supplier name…')}
                                            value={supplierQuery}
                                            onChange={setSupplierQuery}
                                            onClear={() => {
                                                setSupplierQuery('');
                                                props.setFilters('supplier', '', false);
                                            }}
                                            closeOnAction={false}
                                            customIconStyle="small"
                                            isActive={!!props.filters.search.supplier}
                                            onApply={() => {
                                                props.setFilters('supplier', supplierQuery, false);
                                            }}
                                        />
                                    </div>
                                </div>
                            </th>

                            {!!displayedColumns.documentType && (
                                <th className="tw-w-44">
                                    <div className="tw-flex tw-justify-center tw-items-baseline">
                                        <div>{$t('Document Type')}</div>
                                        <div className="tw-ml-2">
                                            <TableFilterModalThreeHandlers
                                                id={`${props.storeKey}_document_type_filter-modal`}
                                                headerText={$t('Displayed Document Types')}
                                                customIconStyle={'small'}
                                                onApply={() => {
                                                    props.setFilters('documentType', documentTypes, true);
                                                }}
                                                applyText={$t('Apply')}
                                                onProcess={showAllDocumentTypes}
                                                processText={$t('Show all')}
                                                showProcess={true}
                                                onCancel={() => showAllDocumentTypes(false)}
                                                showCancel={true}
                                                cancelText={$t('Hide all')}
                                                closeOnAction={false}
                                                contentMaxHeight={'30vh'}
                                                isActive={isFilterActive('documentTypes')}
                                            >
                                                <div>
                                                    <div className="tw-grid tw-grid-cols-3 tw-gap-3">
                                                        {Object.keys(documentTypes).map((key) => {
                                                            const checked = !!documentTypes[key];

                                                            return (
                                                                <div key={key}>
                                                                    <CheckboxInput label={key} checked={checked} onChange={() => toggleDocumentType(key)} />
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </TableFilterModalThreeHandlers>
                                        </div>
                                        <div className="tw-pl-2">
                                            <TableSearchModal
                                                id={`${props.storeKey}_document_type_search-modal`}
                                                headerText={$t('Search Document Type')}
                                                placeholder={$t('Type Document Type…')}
                                                value={documentTypeQuery}
                                                onChange={setDocumentTypeQuery}
                                                onClear={() => {
                                                    setDocumentTypeQuery('');
                                                    props.setFilters('documentType', '', false);
                                                }}
                                                closeOnAction={false}
                                                customIconStyle="small"
                                                isActive={!!props.filters.search.documentType}
                                                onApply={() => {
                                                    props.setFilters('documentType', documentTypeQuery, false);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </th>
                            )}
                            {!!displayedColumns.documentNumber && <th className="tw-w-44">{$t('Document Reference')}</th>}
                            {!!displayedColumns.transactionDate && <th className="tw-w-24">{$t('Transaction Date')}</th>}
                            {!!displayedColumns.glCode && (
                                <th className="tw-w-56">
                                    <div className="tw-flex tw-justify-center tw-items-baseline">
                                        <div>{$t('G/L Code')}</div>
                                        <div className="tw-ml-2">
                                            <TableFilterModalThreeHandlers
                                                id={`${props.storeKey}_gl_code_filter-modal`}
                                                headerText={$t('Displayed GL Codes')}
                                                customIconStyle={'small'}
                                                onApply={() => {
                                                    props.setFilters('glCode', glCodes, true);
                                                }}
                                                applyText={$t('Apply')}
                                                onProcess={showAllGlCodes}
                                                processText={$t('Show all')}
                                                showProcess={true}
                                                onCancel={() => showAllGlCodes(false)}
                                                showCancel={true}
                                                cancelText={$t('Hide all')}
                                                closeOnAction={false}
                                                contentMaxHeight={'30vh'}
                                                isActive={isFilterActive('glCodes')}
                                            >
                                                <div>
                                                    <div className="tw-grid tw-grid-cols-4 tw-gap-3">
                                                        {Object.keys(glCodes).map((key) => {
                                                            const checked = !!glCodes[key];

                                                            return (
                                                                <div key={key}>
                                                                    <CheckboxInput label={key} checked={checked} onChange={() => toggleGlCode(key)} />
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </TableFilterModalThreeHandlers>
                                        </div>
                                        <div className="tw-pl-2">
                                            <TableSearchModal
                                                id={`${props.storeKey}_gl_code_search-modal`}
                                                headerText={$t('Search GL Code')}
                                                placeholder={$t('Type GL code or name…')}
                                                value={glCodeQuery}
                                                onChange={setGlCodeQuery}
                                                onClear={() => {
                                                    setGlCodeQuery('');
                                                    props.setFilters('glCode', '', false);
                                                }}
                                                closeOnAction={false}
                                                customIconStyle="small"
                                                isActive={!!props.filters.search.glCode}
                                                onApply={() => {
                                                    props.setFilters('glCode', glCodeQuery, false);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </th>
                            )}
                            {!props.taxEnabled && !!displayedColumns.rawAmount && <th className="tw-w-44">{$t('Amount')}</th>}
                            {!!props.taxEnabled && (
                                <>
                                    {!!displayedColumns.totalExcludeTax && <th className="tw-w-44">{$t('Total Exc. Tax')}</th>}
                                    {!!displayedColumns.tax && <th className="tw-w-44">{$t('Tax')}</th>}
                                    {!!displayedColumns.total && <th className="tw-w-44">{$t('Total')}</th>}
                                    {!!displayedColumns.taxExcluded && <th className="tw-w-44">{$t('Tax Excluded')}</th>}
                                    {!!displayedColumns.taxRate && <th className="tw-w-44">{$t('Tax Rate')}</th>}
                                </>
                            )}
                            {!!displayedColumns.department && (
                                <th className="tw-w-56">
                                    <div className="tw-flex tw-justify-center tw-items-baseline">
                                        <div>{$t('Department / Cost Centre')}</div>
                                        <div className="tw-ml-2">
                                            <TableFilterModalThreeHandlers
                                                id={`${props.storeKey}_departments_filter-modal`}
                                                headerText={$t('Departments / Cost Centres')}
                                                customIconStyle={'small'}
                                                onApply={() => {
                                                    props.setFilters('department', departments, true);
                                                }}
                                                applyText={$t('Apply')}
                                                onProcess={showAllDepartments}
                                                processText={$t('Show all')}
                                                showProcess={true}
                                                onCancel={() => showAllDepartments(false)}
                                                showCancel={true}
                                                cancelText={$t('Hide all')}
                                                closeOnAction={false}
                                                contentMaxHeight={'30vh'}
                                                isActive={isFilterActive('departments')}
                                            >
                                                <div>
                                                    <div
                                                        className={`tw-grid ${filterableKeys(departments).length < 5 ? 'tw-grid-cols-2' : 'tw-grid-cols-4'} tw-gap-3`}
                                                    >
                                                        {filterableKeys(departments).map((key) => {
                                                            const checked = !!departments[key].show;

                                                            return (
                                                                <div key={key}>
                                                                    <CheckboxInput
                                                                        label={departments[key].name}
                                                                        checked={checked}
                                                                        onChange={() => toggleDepartment(key)}
                                                                    />
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </TableFilterModalThreeHandlers>
                                        </div>
                                        <div className="tw-pl-2">
                                            <TableSearchModal
                                                id={`${props.storeKey}_department_search-modal`}
                                                headerText={$t('Search Department')}
                                                placeholder={$t('Type Department or Cost center…')}
                                                value={departmentQuery}
                                                onChange={setDepartmentQuery}
                                                onClear={() => {
                                                    props.setFilters('department', '', false);
                                                    setDepartmentQuery('');
                                                }}
                                                closeOnAction={false}
                                                customIconStyle="small"
                                                isActive={!!props.filters.search.department}
                                                onApply={() => {
                                                    props.setFilters('department', departmentQuery, false);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </th>
                            )}
                            {!!displayedColumns.type && <th className="tw-w-44">{$t('Type')}</th>}
                            {!!displayedColumns.budgetLine && (
                                <th className="tw-w-56">
                                    <div className="tw-flex tw-justify-center tw-items-baseline">
                                        <div>{$t('Budget Line')}</div>
                                        <div className="tw-ml-2">
                                            <TableFilterModalThreeHandlers
                                                id={`${props.storeKey}_budget_lines_filter-modal`}
                                                headerText={$t('Budget Lines')}
                                                customIconStyle={'small'}
                                                onApply={() => {
                                                    props.setFilters('budgetLine', budgetLines, true);
                                                }}
                                                applyText={$t('Apply')}
                                                onProcess={showAllBudgetLines}
                                                processText={$t('Show all')}
                                                showProcess={true}
                                                onCancel={() => showAllBudgetLines(false)}
                                                showCancel={true}
                                                cancelText={$t('Hide all')}
                                                closeOnAction={false}
                                                contentMaxHeight={'30vh'}
                                                isActive={isFilterActive('budgetLines')}
                                            >
                                                <div>
                                                    <div
                                                        className={`tw-grid ${filterableKeys(budgetLines).length < 5 ? 'tw-grid-cols-2' : 'tw-grid-cols-4'} tw-gap-3`}
                                                    >
                                                        {filterableKeys(budgetLines).map((key) => {
                                                            const checked = !!budgetLines[key].show;

                                                            return (
                                                                <div key={key}>
                                                                    <CheckboxInput
                                                                        label={budgetLines[key].name}
                                                                        checked={checked}
                                                                        onChange={() => toggleBudgetLine(key)}
                                                                    />
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </TableFilterModalThreeHandlers>
                                        </div>
                                        <div className="tw-pl-2">
                                            <TableSearchModal
                                                id={`${props.storeKey}_budget_line_search-modal`}
                                                headerText={$t('Search Budget Line')}
                                                placeholder={$t('Type Budget Line…')}
                                                value={budgetLineQuery}
                                                onChange={setBudgetLineQuery}
                                                onClear={() => {
                                                    setBudgetLineQuery('');
                                                    props.setFilters('budgetLine', '', false);
                                                }}
                                                closeOnAction={false}
                                                customIconStyle="small"
                                                isActive={!!props.filters.search.budgetLine}
                                                onApply={() => {
                                                    props.setFilters('budgetLine', budgetLineQuery, false);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </th>
                            )}
                            {!!displayedColumns.transactionType && <th className="tw-w-44">{$t('Transaction')}</th>}
                            {!!displayedColumns.contractReference && (
                                <th className="tw-w-56">
                                    <div className="tw-flex tw-justify-center tw-items-baseline">
                                        <div>{$t('Contract Reference')}</div>
                                        <div className="tw-ml-2">
                                            <TableFilterModalThreeHandlers
                                                id={`${props.storeKey}_contract_references_filter-modal`}
                                                headerText={$t('Contract References')}
                                                customIconStyle={'small'}
                                                onApply={() => {
                                                    props.setFilters('contractReference', contractReferences, true);
                                                }}
                                                applyText={$t('Apply')}
                                                onProcess={showAllContractReferences}
                                                processText={$t('Show all')}
                                                showProcess={true}
                                                onCancel={() => showAllContractReferences(false)}
                                                showCancel={true}
                                                cancelText={$t('Hide all')}
                                                closeOnAction={false}
                                                contentMaxHeight={'30vh'}
                                                isActive={isFilterActive('contractReferences')}
                                            >
                                                <div>
                                                    <div className={`tw-grid tw-grid-cols-4 tw-gap-3`}>
                                                        {filterableKeys(contractReferences).map((key) => {
                                                            const checked = !!contractReferences[key].show;

                                                            return (
                                                                <div key={key}>
                                                                    <CheckboxInput
                                                                        label={contractReferences[key].name}
                                                                        checked={checked}
                                                                        onChange={() => toggleContractReference(key)}
                                                                    />
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </TableFilterModalThreeHandlers>
                                        </div>
                                        <div className="tw-pl-2">
                                            <TableSearchModal
                                                id={`${props.storeKey}_contract_reference_search-modal`}
                                                headerText={$t('Search Contract Reference')}
                                                placeholder={$t('Type Contract Reference…')}
                                                value={contractReferenceQuery}
                                                onChange={setContractReferenceQuery}
                                                onClear={() => {
                                                    setContractReferenceQuery('');
                                                    props.setFilters('contractReference', '', false);
                                                }}
                                                closeOnAction={false}
                                                customIconStyle="small"
                                                isActive={!!props.filters.search.contractReference}
                                                onApply={() => {
                                                    props.setFilters('contractReference', contractReferenceQuery, false);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </th>
                            )}
                            {!!displayedColumns.amount && <th className="tw-w-44">{$t('Amount')}</th>}
                            {!!displayedColumns.splitAllocation && (
                                <th className="tw-w-72">
                                    <div>{$t('Actions')}</div>
                                    {!!props.confirmActualCosts && (
                                        <ActionsColumnHeader
                                            isSelectionMode={isSelectionMode}
                                            selectedItems={selectedItems}
                                            totalEligibleCount={totalEligibleCount}
                                            buildConfirmPayload={buildConfirmPayload}
                                            confirmActualCosts={props.confirmActualCosts}
                                            actualCostType={props.actualCostType}
                                            exitSelectionMode={exitSelectionMode}
                                            setRowAllocations={setRowAllocations}
                                            setEditedRows={setEditedRows}
                                            confirmAllLoading={confirmAllLoading}
                                            confirmAllDisabled={confirmAllDisabled}
                                            enableSelectionMode={enableSelectionMode}
                                            isReconciled={!!props.isReconciled}
                                        />
                                    )}
                                </th>
                            )}
                        </tr>
                    </thead>
                    {!!props.request && (
                        <tbody>
                            <tr>
                                <td colSpan={colspanVal() + 1}>
                                    <LoadingSpinner />
                                </td>
                            </tr>
                        </tbody>
                    )}
                    {!props.request && (
                        <tbody>
                            {Object.keys(props.data).map((month, key) => {
                                return (
                                    <Fragment key={month}>
                                        <tr>
                                            <td className="tw-cursor-pointer" style={{ zIndex: 14 }} colSpan={2} onClick={() => toggleCollapsed(month)}>
                                                <div className="tw-flex tw-items-center">
                                                    <div className="tw-font-bold tw-pe-2">{formatMonth(month)}</div>
                                                    <MonthCountsBadge
                                                        counts={monthCounts[month]}
                                                        shown={props.data[month].length}
                                                        showShown={columnFilterActive}
                                                    />
                                                    {month in collapsedMonths && (
                                                        <div>
                                                            {collapsedMonths[month] ? (
                                                                <ChevronDownIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal" />
                                                            ) : (
                                                                <ChevronRightIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal" />
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            {colspanVal() > 0 && <td colSpan={colspanVal()}></td>}
                                        </tr>
                                        {month in collapsedMonths && !!collapsedMonths[month] && (
                                            <>
                                                {props.data[month].map((actualCosts, rowIndex) => {
                                                    const hidden = false;

                                                    return (
                                                        <ActualCostsAllocationRow
                                                            glCodes={props.glCodes}
                                                            key={actualCosts.id}
                                                            actualCosts={actualCosts}
                                                            departments={props.departments}
                                                            directLineItems={props.directLineItems}
                                                            saveAllocationUrl={props.saveAllocationUrl}
                                                            fetchData={props.fetchData}
                                                            request={props.request}
                                                            editedRow={editedRows[actualCosts.id] ?? false}
                                                            manuallyOpenedRow={manuallyOpenedRows[actualCosts.id] ?? false}
                                                            setEditedRow={setEdited}
                                                            rowIndex={rowIndex}
                                                            dropAllocationUrl={props.dropAllocationUrl}
                                                            types={props.types}
                                                            transactionTypes={props.transactionTypes}
                                                            typeWorkforce={props.typeWorkforce}
                                                            typeNonWorkforce={props.typeNonWorkforce}
                                                            taxes={props.taxes}
                                                            taxEnabled={props.taxEnabled}
                                                            updateTaxUrl={props.updateTaxUrl}
                                                            setActualCostsItem={(data) => {
                                                                props.setActualCostsItem(month, rowIndex, data);
                                                            }}
                                                            displayedColumns={displayedColumns}
                                                            contracts={getContracts(actualCosts)}
                                                            processActualCostsAfterAllocation={props.processActualCostsAfterAllocation}
                                                            allContracts={props.allContracts}
                                                            hidden={hidden}
                                                            fetchFilters={props.fetchFilters}
                                                            editableGeneralInfo={props.editableGeneralInfo}
                                                            editActualCostGeneralInfoItem={(key, value) =>
                                                                props.editActualCostGeneralInfoItem(month, rowIndex, key, value)
                                                            }
                                                            resetEdit={(datum) => {
                                                                props.resetEdit(month, rowIndex, datum);
                                                            }}
                                                            saveGeneralInfo={props.saveGeneralInfo}
                                                            suppliers={props.suppliers}
                                                            toast={props.toast}
                                                            isSelectable={isSelectionMode}
                                                            isEligible={eligibleIds.has(actualCosts.id)}
                                                            onAllocationsChange={reportRowAllocations}
                                                            cachedAllocations={rowAllocations[actualCosts.id]}
                                                            selected={selectedItems.has(actualCosts.id)}
                                                            bulkApply={bulkApply}
                                                            archiveActualCost={props.archiveActualCost ? archiveActualCost : null}
                                                            toggleSelection={(id) => {
                                                                if (!eligibleIds.has(id)) {
                                                                    return;
                                                                }
                                                                if (selectedItems.has(id)) {
                                                                    setSelectedItems((prev) => new Set([...prev].filter((itemId) => itemId !== id)));
                                                                } else {
                                                                    setSelectedItems((prev) => new Set([...prev, id]));
                                                                }
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </>
                                        )}
                                    </Fragment>
                                );
                            })}
                            <tr></tr>
                        </tbody>
                    )}
                </table>
            </div>
        </div>
    );
}
